import { Component, OnInit, Input, EventEmitter, Output, OnChanges, SimpleChanges, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { cloneFilterState, DataFilterService } from 'src/app/services/data-filter.service';
import { HIGH_RISK_EVENT_LABELS } from 'src/app/data/high-risk-event-options';

type FilterValues = Record<string, unknown>;
type FilterFormatter = (value: unknown, filterValues: FilterValues) => string;

interface FilterPillConfig {
  key: string;
  label: string;
  aliases?: string[];
  formatter?: FilterFormatter;
  masked?: boolean;
  isActive?: (value: unknown, filterValues: FilterValues) => boolean;
  truncateItemAt?: number;
}

export interface ActiveFilterDefinition {
  key: string;
  label: string;
  value: unknown;
  formatter?: FilterFormatter;
  masked: boolean;
  displayValue: string;
  tooltipValue?: string;
}


@Component({
    selector: 'app-data-filter-pills',
    templateUrl: './data-filter-pills.component.html',
    styleUrls: ['./data-filter-pills.component.css'],
    standalone: false
})
export class DataFilterPillsComponent implements OnInit, OnChanges {
  private readonly dataFilterService = inject(DataFilterService);

  @Input() filterValues: FilterValues | null | undefined;
  @Input() deId = false;
  @Output() filterVal = new EventEmitter();
  @Output() filterInputs = new EventEmitter();
  activeFilterDefinitions: ActiveFilterDefinition[] = [];

  private readonly maskedValue = '*******';
  private readonly maxVisibleItems = 3;
  private readonly dateFormatter = (value: unknown): string => formatDate(value as string | number | Date, 'MM-dd-yyyy ', 'en-US');
  private readonly historyDateFormatter = (value: unknown): string => formatDate(value as string | number | Date, 'MM-dd-yyyy', 'en-US');
  private readonly riskLevelFormatter = (): string => this.getRiskLevelDisplay();
  private readonly highRiskEventsFormatter = (value: unknown): string => {
    const values = Array.isArray(value) ? value : [value];

    return values.map((item) => HIGH_RISK_EVENT_LABELS[`${item}`] || `${item}`).join(', ');
  };
  private readonly firstValueIsPresent = (value: unknown): boolean => {
    const firstValue = this.getFirstValue(value);
    return firstValue !== null && firstValue !== undefined;
  };
  private readonly firstValueIsTruthy = (value: unknown): boolean => !!value && !!this.getFirstValue(value);
  private readonly riskLevelIsActive = (value: unknown): boolean => {
    const values = Array.isArray(value) ? value : [value];
    return values.some((val) => val !== null && val !== undefined && val !== '');
  };
  private readonly filterPillConfigs: FilterPillConfig[] = [
    { key: 'startDate', label: 'Start Date', formatter: this.dateFormatter },
    { key: 'transactionStartDate', label: 'Start Date', formatter: this.dateFormatter },
    { key: 'transactionEndDate', label: 'End Date', formatter: this.dateFormatter },
    { key: 'endDate', label: 'End Date', formatter: this.dateFormatter },
    { key: 'adminId', label: 'User ID', masked: true, isActive: this.firstValueIsPresent },
    { key: 'employeeId', label: 'User IDs', masked: true, isActive: this.firstValueIsPresent },
    { key: 'adminName', label: 'User Name', masked: true },
    { key: 'tvs', label: 'TVS' },
    { key: 'location', label: 'Stock Area' },
    { key: 'bedDev', label: 'Stock Area' },
    { key: 'locationGrouping', label: 'Area', isActive: this.firstValueIsTruthy },
    { key: 'hriskEvents', label: 'No of High Risk Events', formatter: this.highRiskEventsFormatter },
    { key: 'followUp', label: 'Follow Up' },
    { key: 'userName', label: 'User Name', masked: true },
    { key: 'device', label: 'Stock Area' },
    { key: 'medDescription', label: 'Med Description', truncateItemAt: 10 },
    { key: 'adminGrouping', label: 'Role' },
    { key: 'p2p_type', label: 'Audit Type' },
    { key: 'p2p', label: 'User Score' },
    { key: 'mrnNum', label: 'Patient ID', masked: true },
    { key: 'UserName', label: 'User Name', masked: true },
    { key: 'employeeName', label: 'User Name', masked: true },
    { key: 'ptname', label: 'Patient Name', masked: true },
    { key: 'UserType', label: 'Role' },
    { key: 'adminType', label: 'Role' },
    { key: 'medDesc', label: 'Medication Description', truncateItemAt: 10 },
    { key: 'uom', label: 'Unit of Measure' },
    { key: 'resolution', label: 'Variance Type' },
    { key: 'UserID', label: 'User ID', masked: true },
    { key: 'PatientID', label: 'Patient ID', masked: true },
    { key: 'PatientName', label: 'Patient Name', masked: true },
    { key: 'MedDescription', label: 'Medication Description', truncateItemAt: 10 },
    { key: 'Device', label: 'Stock Area' },
    { key: 'TransactionType', label: 'Transaction Type' },
    { key: 'RecordType', label: 'Record Type' },
    { key: 'riskScoreFlag', label: 'Record Type' },
    { key: 'medGroupName', label: 'Med Group Name', truncateItemAt: 10 },
    { key: 'medGroup', label: 'Med Group Name', truncateItemAt: 10 },
    { key: 'shiftworked', label: 'Shift' },
    { key: 'userID', aliases: ['userId'], label: 'User ID', masked: true },
    { key: 'practiceIssue', label: 'Practice Issue' },
    { key: 'riskLevel', label: 'Risk Level', formatter: this.riskLevelFormatter, isActive: this.riskLevelIsActive },
    { key: 'transType', label: 'Transaction Type' },
    { key: 'csn', label: 'CSN' },
    { key: 'historyDate', label: 'History Date', formatter: this.historyDateFormatter },
    { key: 'source', label: 'Source' }
  ];

  ngOnInit(): void {
    this.refreshActiveFilterDefinitions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterValues || changes.deId) {
      this.refreshActiveFilterDefinitions();
    }
  }

  refreshActiveFilterDefinitions(): void {
    const filterValues = this.filterValues;

    if (!filterValues) {
      this.activeFilterDefinitions = [];
      return;
    }

    this.activeFilterDefinitions = this.filterPillConfigs.reduce((activeFilters: ActiveFilterDefinition[], config: FilterPillConfig) => {
      const activeKey = this.resolveFilterKey(config, filterValues);

      if (!activeKey) {
        return activeFilters;
      }

      const value = filterValues[activeKey];
      const masked = !!config.masked && this.deId;

      activeFilters.push({
        key: activeKey,
        label: config.label,
        value,
        formatter: config.formatter,
        masked,
        displayValue: masked ? this.maskedValue : this.formatFilterValue(value, config, filterValues),
        tooltipValue: masked ? undefined : this.getTooltipValue(value, config)
      });

      return activeFilters;
    }, []);
  }

  closePill(key: string) {
    try {
      if (!this.filterValues) {
        return;
      }

      const updatedFilterValues = cloneFilterState(this.filterValues);

      // Handle date-related filters
      if (['startDate', 'transactionStartDate', 'endDate', 'transactionEndDate'].includes(key)) {
        if (key.includes('transaction')) {
          delete updatedFilterValues.transactionEndDate;
          delete updatedFilterValues.transactionStartDate;
        } else {
          delete updatedFilterValues.endDate;
          delete updatedFilterValues.startDate;
        }
      } else if (key.includes('history')) {
        delete updatedFilterValues.historyDate;
        delete updatedFilterValues.historyAdminId;
      } else {
        // Delete the specific filter
        delete updatedFilterValues[key];
      }

      // Treat @Input state as read-only. The owning component updates the pills
      // after it has promoted this new filter to applied state.
      this.filterVal.emit(cloneFilterState(updatedFilterValues));

    } catch (error) {
      console.warn('Error removing filter:', error);
    }
  }

  // Method to explicitly open filter modal
  openFilterModal() {
    this.filterInputs.emit(this.filterValues ? cloneFilterState(this.filterValues) : this.filterValues);
  }

  getRiskLevelDisplay(): string {
    const riskLevel = this.filterValues?.riskLevel;
    if (!riskLevel) return '';
    const values = Array.isArray(riskLevel) ? riskLevel : [riskLevel];
    return values
      .filter((val: unknown) => val !== null && val !== undefined && val !== '')
      .map((val: unknown) => val === 'tvs' ? 'All' : String(val))
      .join(', ');
  }

  trackByFilterKey(_index: number, filter: ActiveFilterDefinition): string {
    return filter.key;
  }

  private resolveFilterKey(config: FilterPillConfig, filterValues: FilterValues): string | undefined {
    const keys = [config.key, ...(config.aliases || [])];

    return keys.find((key) => {
      const value = filterValues[key];
      return config.isActive
        ? config.isActive(value, filterValues)
        : this.dataFilterService.isActive(value);
    });
  }

  private formatFilterValue(value: unknown, config: FilterPillConfig, filterValues: FilterValues): string {
    if (config.formatter) {
      return config.formatter(value, filterValues);
    }

    if (value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      const items = config.truncateItemAt
        ? value.map((item) => {
            const str = `${item}`;
            return str.length > config.truncateItemAt! ? str.slice(0, config.truncateItemAt) + '..' : str;
          })
        : value.map((item) => `${item}`);
      if (items.length > this.maxVisibleItems) {
        return items.slice(0, this.maxVisibleItems).join(', ') + ` (+${items.length - this.maxVisibleItems} more)`;
      }
      return items.join(', ');
    }

    return `${value}`;
  }

  private getTooltipValue(value: unknown, config: FilterPillConfig): string | undefined {
    if (config.formatter) return undefined;
    if (!Array.isArray(value)) return undefined;
    const hasMoreThanVisible = value.length > this.maxVisibleItems;
    const hasItemTruncation = !!config.truncateItemAt && value.some((item) => `${item}`.length > config.truncateItemAt!);
    if (!hasMoreThanVisible && !hasItemTruncation) return undefined;
    return value.join(', ');
  }

  private getFirstValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value[0];
    }

    return value;
  }

}
